import { useEffect, useRef } from 'react';
import { useT } from '../i18n';
import './Acknowledgement.css';

interface AcknowledgementProps {
  id: string;
  email: string;
}

/**
 * The end of the flow. Deliberately contains no number, estimate or range —
 * there is no automated valuation, and implying one here would be a promise the
 * product cannot keep.
 */
export function Acknowledgement({ id, email }: AcknowledgementProps) {
  const t = useT();
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  // The page changed underneath the owner without a navigation; move focus so a
  // screen reader lands on the outcome instead of staying on a vanished button.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <section className="ack" aria-labelledby="ack-heading">
      <p className="eyebrow">{t.success.eyebrow}</p>

      {/* The confirmation is the heading; what happens next is the paragraph
          under it. Splitting them lets the fact land on its own line at
          display size instead of being buried mid-sentence. */}
      <h1 id="ack-heading" ref={headingRef} tabIndex={-1}>
        {t.success.headline}
      </h1>

      <p className="lede ack__body">{t.success.body}</p>

      <p className="ack__signature">{t.success.signature}</p>

      {/* The practical details live in one glass panel — the same vocabulary as
          the step-3 summary — instead of loose lines on the page. */}
      <div className="ack__panel">
        <div className="ack__meta-row">
          <span className="eyebrow">{t.success.referenceLabel}</span>
          {/* Selectable: this is a string the owner may need to
              copy into an email to us. */}
          <span className="ack__reference">{id}</span>
        </div>
        <p className="ack__note">{t.success.referenceHelp}</p>
        <p className="ack__note">{t.success.emailedTo(email)}</p>
      </div>

      {/*
        What arrives in 3–5 days, closing the page. The heading sits OUTSIDE the
        panel — it titles the block rather than labelling a row inside it, which
        is what it did on step 3.
      */}
      <section className="ack__deliverable" aria-labelledby="ack-deliverable-heading">
        <h2 className="ack__deliverable-heading" id="ack-deliverable-heading">
          {t.success.deliverableHeading}
        </h2>
        <ul className="ack__deliverable-list">
          {t.success.deliverableItems.map((item) => (
            <li className="ack__deliverable-item" key={item}>
              {item}
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}
